import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { CheckCircle, Download, RotateCcw, Users, Plus, Minus, MapPin, Loader2, Copy, MessageCircle, X, Trash2, Save, History } from 'lucide-react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import './DispatchPlanner.css';
import { dispatchAPI } from '../../../config/dispatch-api';
import { API_CONFIG } from '../../../config/api';
import { IS_ALFAJR } from '../../../constants/general';
import { toast } from 'react-toastify';
import { getMapboxToken, formatDateOnly, normalizeToYYYYMMDD, getBatchDirections } from '../../../utils/utils';
import { helpers } from '../../../utils/helpers';
import { ADMIN_PERMISSIONS } from '../../../constants/permissions';

mapboxgl.accessToken = getMapboxToken();

const getPlanOrderIds = (routePlans) => {
  const ids = new Set();
  (routePlans || []).forEach((plan) => {
    (plan.stops || []).forEach((stop) => {
      if (stop.order) ids.add(String(stop.order));
    });
  });
  return ids;
};

const buildLockedAssignmentsFromPlans = (plans, override = {}) => {
  const locked = {};
  (plans || []).forEach((plan) => {
    (plan.stops || []).forEach((stop) => {
      if (stop?.order != null && stop.order !== '') {
        locked[String(stop.order)] = plan.driver;
      }
    });
  });
  Object.entries(override).forEach(([orderId, driver]) => {
    locked[String(orderId)] = driver;
  });
  return locked;
};

const shouldCreateAdditionalRoute = (routePlans, savedOrderIds) => {
  const planOrderIds = getPlanOrderIds(routePlans);
  if (planOrderIds.size === 0) return false;
  return [...planOrderIds].every((orderId) => !savedOrderIds.has(orderId));
};

const DispatchPlanner = ({ onUnsavedChange, adminData, onPageChange }) => {
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [selectedTime, setSelectedTime] = useState('ALL ORDERS');
  const [timeSlots, setTimeSlots] = useState([{ name: 'ALL ORDERS' }]);
  const [fetchedOrders, setFetchedOrders] = useState([]);
  const [selectedOrders, setSelectedOrders] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [routeData, setRouteData] = useState(null);
  const [availableDrivers, setAvailableDrivers] = useState([]);
  const [selectedDrivers, setSelectedDrivers] = useState([]);
  const [isLoadingDrivers, setIsLoadingDrivers] = useState(false);
  const [existingRouteId, setExistingRouteId] = useState(null);
  const [slotConflict, setSlotConflict] = useState(false);
  const [usedOrderIds, setUsedOrderIds] = useState(new Set());
  const [plans, setPlans] = useState([]);
  const [locked, setLocked] = useState({});
  const [currentPopup, setCurrentPopup] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showUnsavedModal, setShowUnsavedModal] = useState(false);
  const [isSaveLoading, setIsSaveLoading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const [hasFetchedOnce, setHasFetchedOnce] = useState(false);
  const [showResetModal, setShowResetModal] = useState(false);
  const [storeCoordinates, setStoreCoordinates] = useState(null);

  const mapContainer = useRef(null);
  const map = useRef(null);
  const markers = useRef([]);
  const routeLayers = useRef([]);
  const depotMarker = useRef(null);
  const existingRouteIdRef = useRef(null);
  const lockedRef = useRef({});
  const routeDataRef = useRef(null);
  const isSavingRef = useRef(false);
  const plansRef = useRef([]);
  const rebuildTimerRef = useRef(null);
  const pendingRebuildDriversRef = useRef(null);

  // Default Warehouse/Depot coordinates 
  const DEFAULT_DEPOT = [51.427533, 25.200859]; // [lon, lat]
  const DEPOT = storeCoordinates || DEFAULT_DEPOT;

  const getDepotLabel = () => {
    try {
      const adminData = localStorage.getItem('adminData');
      if (adminData) {
        const parsed = JSON.parse(adminData);
        const name = parsed?.currentStore?.storeName || parsed?.storeName;
        if (name) return name;
      }
    } catch (e) {
      console.error('Error parsing adminData for store name:', e);
    }
    return IS_ALFAJR ? 'Al Fajr Store' : 'Able Mart Base';
  };
  const DEPOT_LABEL = getDepotLabel();

  useEffect(() => {
    if (!map.current || !mapContainer.current) return;

    const resizeObserver = new ResizeObserver(() => {
      if (map.current) {
        map.current.resize();
      }
    });

    resizeObserver.observe(mapContainer.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  const getDefaultTimeSlot = (slots) => {
    if (!slots || slots.length === 0) return 'ALL ORDERS';

    const now = new Date();
    const qatarTime = new Date(
      now.toLocaleString("en-US", { timeZone: "Asia/Qatar" })
    );
    const hour = qatarTime.getHours();

    // Find matching slot based on current hour
    for (const slot of slots) {
      const startHour = parseInt(slot.startTime?.split(':')[0] || '0');
      if (hour >= startHour && hour < startHour + 2) {
        return slot.name;
      }
    }

    return slots[0]?.name || 'ALL ORDERS';
  };

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Warn on browser navigation/close if unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = '';
        return '';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  // Sync unsaved changes state to parent
  useEffect(() => {
    if (onUnsavedChange) {
      onUnsavedChange(hasUnsavedChanges);
    }
  }, [hasUnsavedChanges, onUnsavedChange]);

  useEffect(() => {
    plansRef.current = plans;
  }, [plans]);


  // Initialize map
  useEffect(() => {
    if (map.current) return;

    map.current = new mapboxgl.Map({
      container: mapContainer.current,
      style: 'mapbox://styles/mapbox/streets-v12',
      center: DEPOT, // Warehouse coordinates
      zoom: 10.8
    });

    map.current.addControl(new mapboxgl.NavigationControl({ showCompass: false }), 'top-right');

    // Custom Fullscreen control with provided SVG icon
    (function () {
      function FullscreenButtonControl() { this._map = null; this._container = null; }
      FullscreenButtonControl.prototype.onAdd = function (map) {
        this._map = map;
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.title = 'Fullscreen';
        btn.setAttribute('aria-label', 'Fullscreen');
        btn.style.background = '#fff';
        btn.style.border = 'none';
        btn.style.width = '30px';
        btn.style.height = '30px';
        btn.style.borderRadius = '4px';
        btn.style.boxShadow = '0 1px 2px rgba(0,0,0,0.15)';
        btn.style.cursor = 'pointer';
        btn.style.display = 'flex';
        btn.style.alignItems = 'center';
        btn.style.justifyContent = 'center';
        btn.innerHTML = '<svg width="22" height="22" viewBox="0 0 25 25" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M4.05469 16.25C4.05469 15.8358 4.39047 15.5 4.80469 15.5C5.2189 15.5 5.55469 15.8358 5.55469 16.25V19.5H8.80469L8.88086 19.5039C9.25912 19.5422 9.55469 19.8616 9.55469 20.25C9.55469 20.6384 9.25912 20.9578 8.88086 20.9961L8.80469 21H4.80469C4.39047 21 4.05469 20.6642 4.05469 20.25V16.25ZM20.0537 16.25C20.0537 15.8358 20.3895 15.5 20.8037 15.5C21.2177 15.5003 21.5537 15.8359 21.5537 16.25V20.25C21.5537 20.4488 21.4745 20.6396 21.334 20.7803C21.1934 20.9208 21.0025 20.9999 20.8037 21H16.8037C16.3895 21 16.0537 20.6642 16.0537 20.25C16.0537 19.8358 16.3895 19.5 16.8037 19.5H20.0537V16.25ZM4.05469 8.25L4.05273 4.25C4.05273 4.05116 4.13188 3.86037 4.27246 3.71973C4.41311 3.57908 4.60382 3.5 4.80273 3.5H8.80469L8.88086 3.50391C9.25912 3.54225 9.55469 3.86163 9.55469 4.25C9.55469 4.63837 9.25912 4.95775 8.88086 4.99609L8.80469 5H5.55273L5.55469 8.25C5.55469 8.66405 5.21878 8.9998 4.80469 9C4.39064 9 4.05489 8.66409 4.05469 8.25ZM20.0537 8.25V5H16.8037C16.3895 5 16.0537 4.66421 16.0537 4.25C16.0537 3.83579 16.3895 3.5 16.8037 3.5H20.8037L20.8799 3.50391C21.2581 3.5423 21.5537 3.86167 21.5537 4.25V8.25C21.5537 8.66405 21.2177 8.99974 20.8037 9C20.3895 9 20.0537 8.66421 20.0537 8.25Z" fill="#343C54"/><path opacity="0.4" d="M12.8027 11.1895L18.9922 5H20.0537V6.06055L13.8643 12.25L20.0537 18.4395V19.5H18.9922L12.8027 13.3105L6.61523 19.5H5.55469V18.4385L11.7422 12.25L5.55273 6.06055V5H6.61328L12.8027 11.1895Z" fill="#343C54"/></svg>';
        btn.onclick = () => {
          const container = map.getContainer();
          const isFull = document.fullscreenElement === container;
          if (isFull) { document.exitFullscreen && document.exitFullscreen(); }
          else { container.requestFullscreen && container.requestFullscreen(); }
        };
        const wrapper = document.createElement('div');
        wrapper.className = 'mapboxgl-ctrl mapboxgl-ctrl-group';
        wrapper.style.background = 'transparent';
        wrapper.style.border = 'none';
        wrapper.appendChild(btn);
        this._container = wrapper;
        return wrapper;
      };
      FullscreenButtonControl.prototype.onRemove = function () {
        if (this._container && this._container.parentNode) this._container.parentNode.removeChild(this._container);
        this._map = null; this._container = null;
      };
      map.current.addControl(new FullscreenButtonControl(), 'top-right');
    })();

    // Add depot marker on map initialization
    if (!depotMarker.current) {
      depotMarker.current = new mapboxgl.Marker({ color: '#000000', scale: 1.2 })
        .setLngLat(DEPOT)
        .setPopup(new mapboxgl.Popup().setHTML(`
          <div style="padding: 8px; text-align: center;">
            <h3 style="margin: 0 0 4px 0; color: #FE8403; font-size: 14px; font-weight: 600;">🏢 ${DEPOT_LABEL}</h3>
            <p style="margin: 0; color: #666; font-size: 12px;">Starting Point</p>
          </div>
        `))
        .addTo(map.current);
    }
  }, [DEPOT, DEPOT_LABEL]);

  // Keep depot marker location synced when storeCoordinates load
  useEffect(() => {
    if (map.current && depotMarker.current) {
      depotMarker.current.setLngLat(DEPOT);
    }
  }, [DEPOT]);

  // Add beforeunload listener for unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  // Fetch drivers on component mount
  useEffect(() => {
    const loadDispatchDetails = async () => {
      setIsLoadingDrivers(true);
      try {
        const response = await dispatchAPI.getDispatchDetails(selectedDate);
        if (response.success && response.data) {
          // Set time slots
          const slots = response.data.timeSlots || [];
          const allSlots = [{ name: 'ALL ORDERS' }, ...slots];
          setTimeSlots(allSlots);
          setSelectedTime(getDefaultTimeSlot(slots));

          // Set drivers
          const drivers = (response.data.drivers || [])
            .filter(d => d.status !== false)
            .map(driver => ({
              _id: driver._id,
              name: driver.name,
              phone: driver.phoneNumber || '',
              color: driver.color || '#FE8403',
              status: driver.status
            }));
          setAvailableDrivers(drivers);

          if (response.data.storeCoordinates && Array.isArray(response.data.storeCoordinates) && response.data.storeCoordinates.length === 2) {
            setStoreCoordinates(response.data.storeCoordinates);
            // Update map center to store coordinates
            if (map.current) {
              map.current.flyTo({ center: response.data.storeCoordinates, zoom: 10.8 });
            }
          }
        } else {
          setAvailableDrivers([]);
        }
      } catch (error) {
        console.error('Error loading dispatch details:', error);
        setAvailableDrivers([]);
      } finally {
        setIsLoadingDrivers(false);
      }
    };
    loadDispatchDetails();
  }, [selectedDate]);

  // Clear markers
  const clearMarkers = (keepDepot = false) => {
    markers.current.forEach(marker => marker.remove());
    markers.current = [];
    if (!keepDepot && depotMarker.current) {
      depotMarker.current.remove();
      depotMarker.current = null;
    }
  };

  // Add Depot Marker Helper
  const ensureDepotMarker = () => {
    if (map.current && !depotMarker.current) {
      depotMarker.current = new mapboxgl.Marker({ color: '#000000', scale: 1.2 })
        .setLngLat(DEPOT)
        .setPopup(new mapboxgl.Popup({ className: 'dispatch-order-popup' }).setHTML(`
          <div style="padding: 8px; text-align: center;">
            <h3 style="margin: 0 0 4px 0; color: #FE8403; font-size: 14px; font-weight: 600;">🏢 ${DEPOT_LABEL}</h3>
            <p style="margin: 0; color: #666; font-size: 12px;">Starting Point</p>
          </div>
        `))
        .addTo(map.current);
    }
  };

  // Add markers for orders
  const addOrderMarkers = (orders) => {
    clearMarkers(true); // Keep depot marker
    ensureDepotMarker();
    const color = '#f97316';

    orders.forEach((order, index) => {
      const markerElement = document.createElement("div");
      markerElement.style.cssText = `
        background: ${color};
        color: white;
        padding: 6px 12px; 
        border-radius: 16px;
        font-size: 12px;
        font-weight: 500;
        border: none;
        box-shadow: 0 1px 3px rgba(0,0,0,0.2);
        cursor: pointer;
        text-align: center;
        min-width: 24px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        white-space: nowrap;
        gap: 6px;
        transition: all 0.2s ease; 
      `;

      const stopCircle = document.createElement("div");
      stopCircle.style.cssText = `
        background: rgba(255,255,255,0.95);
        color: ${color};
        width: 22px;
        height: 22px;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 11px;
        font-weight: 700;
        flex-shrink: 0;
        border: 2px solid rgba(255,255,255,0.3);
        transition: all 0.2s ease;
      `;
      stopCircle.textContent = index + 1;

      const orderText = document.createElement("span");
      orderText.style.fontWeight = '600';
      orderText.textContent = order.order || "?";

      markerElement.appendChild(stopCircle);
      markerElement.appendChild(orderText);

      const popup = new mapboxgl.Popup({ offset: 25, className: 'dispatch-order-popup' }).setHTML(
        `<div style="padding: 8px;">
          <strong>${order.order}</strong><br/>
          ${order.customer}<br/>
          <span style="color: #f97316; font-weight: 600;">${order.total} QR</span>
        </div>`
      );

      const marker = new mapboxgl.Marker({ element: markerElement })
        .setLngLat(order.coordinates)
        .setPopup(popup)
        .addTo(map.current);

      markers.current.push(marker);
    });
  };

  // Reset when time slot or date changes
  useEffect(() => {
    // Reset orders and routes when time slot or date changes
    setFetchedOrders([]);
    setSelectedOrders([]);
    setRouteData(null);
    clearMarkers(false); // Clear all including depot (to refresh)

    // Clear route layers
    routeLayers.current.forEach(layerId => {
      if (map.current && map.current.getLayer(layerId)) {
        map.current.removeLayer(layerId);
      }
      if (map.current && map.current.getSource(layerId)) {
        map.current.removeSource(layerId);
      }
    });
    routeLayers.current = [];

    // Reset map view
    if (map.current) {
      map.current.flyTo({
        center: DEPOT,
        zoom: 10.8
      });
      ensureDepotMarker();
    }

    // Check for existing route when slot/date changes
    const checkExistingRoute = async () => {
      try {
        if (selectedTime === 'ALL ORDERS') {
          setExistingRouteId(null);
          existingRouteIdRef.current = null;
          setSlotConflict(false);
          setUsedOrderIds(new Set());
          return;
        }

        const { usedOrderSet, existingRoute } = await dispatchAPI.checkUsedOrdersAndRoute(selectedTime, selectedDate);
        setUsedOrderIds(usedOrderSet);

        if (existingRoute) {
          const routeId = existingRoute._id || existingRoute.id || existingRoute.route_id || null;
          setSlotConflict(true);
          setExistingRouteId(routeId);
          existingRouteIdRef.current = routeId;
        } else {
          setSlotConflict(false);
          setExistingRouteId(null);
          existingRouteIdRef.current = null;
        }
      } catch (error) {
        console.error('Error checking existing route:', error);
      }
    };

    checkExistingRoute();
  }, [selectedTime, selectedDate]);

  // Route History Functions
  const checkUsedOrdersAndReturn = async (currentSlot, currentDate) => {
    try {
      const { usedOrderSet } = await dispatchAPI.checkUsedOrdersAndRoute(currentSlot, currentDate);
      setUsedOrderIds(usedOrderSet);
      return usedOrderSet;
    } catch (error) {
      console.error('Error checking used orders:', error);
      return new Set();
    }
  };

  const saveRouteToHistory = async (plans, timeSlot, date, slotId = null) => {
    try {
      const result = await dispatchAPI.saveRouteToHistory(plans, timeSlot, date, slotId);
      return result;
    } catch (error) {
      console.error('Error saving route to history:', error);
      throw error;
    }
  };

  const updateRouteInHistory = async (plans, timeSlot, date, slotId = null) => {
    try {
      const currentRouteId = existingRouteIdRef.current || existingRouteId;
      if (currentRouteId) {
        const result = await dispatchAPI.updateRouteInHistory(currentRouteId, plans, timeSlot, date, slotId);
        return result;
      } else {
        const result = await saveRouteToHistory(plans, timeSlot, date, slotId);
        return result;
      }
    } catch (error) {
      console.error('Error updating route in history:', error);
      throw error;
    }
  };

  // Sync existingRouteIdRef with existingRouteId state
  useEffect(() => {
    existingRouteIdRef.current = existingRouteId;
  }, [existingRouteId]);

  // Sync routeDataRef
  useEffect(() => {
    routeDataRef.current = routeData;
  }, [routeData]);

  useEffect(() => {
    if (fetchedOrders.length === 0) return;
    if (plans && plans.length > 0) return;
    const selectedOnly = fetchedOrders.filter(o =>
      selectedOrders.includes(o.order)
    );
    addOrderMarkers(selectedOnly);
  }, [selectedOrders, plans]);

  // Reset ONLY routes and map logic (keep fetched orders and selected drivers)
  const resetRoutesOnly = () => {
    setRouteData(null);
    setPlans([]);
    setHasUnsavedChanges(false);

    // Clear route layers only
    routeLayers.current.forEach(layerId => {
      if (map.current && map.current.getLayer(layerId)) {
        map.current.removeLayer(layerId);
      }
      if (map.current && map.current.getSource(layerId)) {
        map.current.removeSource(layerId);
      }
    });
    routeLayers.current = [];

    // Clear all current markers
    markers.current.forEach(marker => marker.remove());
    markers.current = [];

    // Re-draw original order markers for the selected orders
    if (fetchedOrders.length > 0) {
      const selectedOnly = fetchedOrders.filter(o =>
        selectedOrders.includes(o.order)
      );
      addOrderMarkers(selectedOnly);
    }
  };

  // Fetch orders
  const fetchOrders = async () => {
    resetRoutesOnly();
    setIsLoading(true);
    try {
      const response = await dispatchAPI.getConfirmedOrders(selectedDate, selectedTime);

      if (response.success && response.data) {
        const transformedOrders = response.data.map(order => ({
          id: order.id,
          order: order.order,
          customer: order.customer,
          total: order.total,
          coordinates: order.coordinates || [],
          timeSlot: order.timeSlot,
          timeSlotId: order.timeSlotId || null,
          deliveryDate: normalizeToYYYYMMDD(order.deliveryDate),
          deliveryStaffUrl: order.deliveryUrl
        })).filter(order => order.coordinates && order.coordinates.length === 2);

        setFetchedOrders(transformedOrders);

        if (selectedTime !== 'ALL ORDERS') {
          const usedOrderSet = await checkUsedOrdersAndReturn(selectedTime, selectedDate);

          const unusedOrders = transformedOrders.filter(order => !usedOrderSet.has(order.order));
          setSelectedOrders(unusedOrders.map(order => order.order));

          const usedCount = transformedOrders.length - unusedOrders.length;
          if (usedCount > 0) {
            toast.info(`Fetched ${transformedOrders.length} orders (${usedCount} already used, ${unusedOrders.length} available). Available orders selected.`);
          } else {
            setSelectedOrders(transformedOrders.map(order => order.order));
          }
        } else {
          setSelectedOrders(transformedOrders.map(order => order.order));
        }
      } else {
        setFetchedOrders([]);
        setSelectedOrders([]);
        toast.error(response.msg || 'Failed to fetch orders');
      }
    } catch (error) {
      console.error('Error fetching orders:', error);
      toast.error('Failed to fetch orders. Please try again.');
      setFetchedOrders([]);
      setSelectedOrders([]);
    } finally {
      setIsLoading(false);
      setHasFetchedOnce(true);
    }
  };

  // Build routes 
  const buildRoutes = useCallback(async () => {
    if (selectedOrders.length === 0) {
      toast.error('Please select orders to build routes');
      return;
    }

    if (selectedDrivers.length === 0) {
      toast.error('Please select at least one driver');
      return;
    }

    setIsLoading(true);

    try {
      const ordersToPlan = fetchedOrders.filter(order =>
        selectedOrders.includes(order.order)
      );

      const transformedOrders = ordersToPlan.map(order => ({
        id: order.id,
        order: order.order,
        customer: order.customer,
        total: order.total,
        coordinates: order.coordinates,
        timeSlot: order.timeSlot,
        deliveryDate: order.deliveryDate,
        deliveryStaffUrl: order.deliveryStaffUrl
      })).filter(order => order.coordinates && order.coordinates.length === 2);

      const driverNames = selectedDrivers.map(driver => driver.name);

      if (selectedTime === 'ALL ORDERS') {
        toast.error('Please select a specific time slot to build routes');
        setIsLoading(false);
        return;
      }

      // Build drivers array with full details
      const driversPayload = selectedDrivers.map(driver => ({
        name: driver.name,
        id: driver._id,
        color: driver.color
      }));

      const normalizedLocks = {};
      Object.entries(lockedRef.current || locked || {}).forEach(([orderId, driver]) => {
        normalizedLocks[String(orderId)] = driver;
      });

      const payload = {
        slot: selectedTime,
        date: selectedDate,
        serviceMin: 5,
        drivers: driversPayload,
        orders: transformedOrders,
        lockedAssignments: normalizedLocks
      };

      const response = await dispatchAPI.planRoutes(payload);

      if (response.success && response.data) {
        const plans = response.data.plans || [];
        setPlans(plans);
        setRouteData({
          drivers: plans.map(plan => ({
            name: plan.driver,
            totalDistance: `${plan.distance_km.toFixed(2)} km`,
            totalTime: plan.total_hhmm,
            returnETA: plan.return_eta,
            driverLoad: plan.load,
            orderCount: plan.stops?.length || 0
          })),
          plans: plans,
          startTime: response.data.start_time,
          returnTimeLatest: response.data.return_time_latest
        });
        setHasUnsavedChanges(true);

        await drawMultipleRoutes(plans);

        if (response.data.rejected_orders > 0) {
          toast.warning(`${response.data.rejected_orders} orders could not be assigned due to capacity constraints.`);
        } else {
          toast.success('Routes built successfully!');
        }

      } else {
        toast.error(response.msg || 'Failed to build routes');
      }
    } catch (error) {
      console.error('Error building routes:', error);
      toast.error(error.message || 'Failed to build routes. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [selectedOrders, selectedDrivers, fetchedOrders, selectedDate, selectedTime, locked]);

  // Draw multiple routes on map
  const drawMultipleRoutes = async (plans) => {
    if (!map.current) return;

    // Clear existing routes
    for (let i = 0; i < 10; i++) {
      const srcId = `route-${i}`;
      const lyrId = `route-${i}-line`;
      if (map.current.getLayer(lyrId)) {
        map.current.removeLayer(lyrId);
      }
      if (map.current.getSource(srcId)) {
        map.current.removeSource(srcId);
      }
    }

    // Clear existing markers
    const existingMarkers = document.querySelectorAll('.mapboxgl-marker');
    existingMarkers.forEach(marker => marker.remove());
    markers.current = [];

    // Initialize bounds with depot
    let bounds = new mapboxgl.LngLatBounds(DEPOT, DEPOT);
    bounds.extend(DEPOT);

    // Add Able Mart base marker
    if (depotMarker.current) {
      depotMarker.current.remove();
    }
    depotMarker.current = new mapboxgl.Marker({ color: '#000000', scale: 1.2 })
      .setLngLat(DEPOT)
      .setPopup(new mapboxgl.Popup().setHTML(`
        <div style="padding: 8px; text-align: center;">
          <h3 style="margin: 0 0 4px 0; color: #FE8403; font-size: 14px; font-weight: 600;">🏢 ${DEPOT_LABEL}</h3>
          <p style="margin: 0; color: #666; font-size: 12px;">Starting Point</p>
        </div>
      `))
      .addTo(map.current);

    // Process each route plan
    for (let i = 0; i < plans.length; i++) {
      const plan = plans[i];
      if (!plan.stops || plan.stops.length === 0) continue;

      // Use plan.color from backend if available, otherwise fall back to getDriverColor
      const color = plan.color || getDriverColor(plan.driver);

      // Filter valid stops and create sequence
      const validStops = plan.stops
        .filter(s => s && s.coord && s.coord.lat != null && s.coord.lon != null)
        .map(s => [parseFloat(s.coord.lon), parseFloat(s.coord.lat)]);

      if (validStops.length === 0) continue;

      // Create linear route: stop1 -> stop2 -> ... -> stopN (no loop back)
      const seq = validStops;

      // Batch route segment coordinates for optimization (avoid loop-based individual calls)
      const coords = await getBatchDirections(seq, mapboxgl.accessToken, 'driving-traffic');
      coords.forEach(c => bounds.extend(c));

      const srcId = `route-${i}`;
      const lyrId = `route-${i}-line`;

      if (map.current.getLayer(lyrId)) {
        map.current.removeLayer(lyrId);
      }
      if (map.current.getSource(srcId)) {
        map.current.removeSource(srcId);
      }

      map.current.addSource(srcId, {
        type: 'geojson',
        data: {
          type: 'Feature',
          geometry: {
            type: 'LineString',
            coordinates: coords
          }
        }
      });
      routeLayers.current.push(srcId);

      if (map.current.getLayer(lyrId)) map.current.removeLayer(lyrId);
      map.current.addLayer({
        id: lyrId,
        type: 'line',
        source: srcId,
        layout: {
          'line-join': 'round',
          'line-cap': 'round'
        },
        paint: {
          'line-color': color,
          'line-width': 5,
          'line-opacity': 0.9
        }
      });
      routeLayers.current.push(lyrId);

      // Add markers for stops 
      plan.stops.filter(s => s && s.coord && s.coord.lat != null && s.coord.lon != null).forEach((stop, stopIndex) => {
        // Extend bounds to include this marker
        bounds.extend([parseFloat(stop.coord.lon), parseFloat(stop.coord.lat)]);

        const dOpts = selectedDrivers.map(d =>
          `<option value="${d.name}" ${plan.driver === d.name ? 'selected' : ''}>${d.name}</option>`
        ).join('');

        const popup = new mapboxgl.Popup({
          offset: 24,
          closeButton: true,
          closeOnClick: false,
          maxWidth: '260px'
        }).setHTML(`
          <div style="width: 240px; padding: 12px; box-sizing: border-box;">
            <div style="margin-bottom: 12px; padding-bottom: 6px; border-bottom: 1px solid #e5e7eb;">
              <h3 style="margin: 0; font-size: 14px; font-weight: 600; color: #111827;">Stop ${stopIndex + 1}</h3>
            </div>
            
            <div style="margin-bottom: 12px;">
              <select id="drv_${stop.order}" style="width: 100%; max-width: 100%; padding: 6px 10px; border: 2px solid #3b82f6; border-radius: 16px; font-size: 13px; font-weight: 600; background: white; color: #111827; outline: none; box-sizing: border-box;">
                ${dOpts}
              </select>
            </div>
            
            <div style="font-size: 12px; line-height: 1.4; color: #374151;">
              <div style="margin-bottom: 4px;"><strong>Order #:</strong> ${stop.order || ''}</div>
              <div style="margin-bottom: 4px;"><strong>Customer:</strong> ${stop.customer || ''}</div>
              <div style="margin-bottom: 4px;"><strong>ETA (arrival):</strong> ${stop.eta_arrival || ''}</div>
              <div style="margin-bottom: 4px;"><strong>Leg time:</strong> ${(stop.leg_minutes || 0).toFixed(1)} min</div>
              <div style="margin-bottom: 4px;"><strong>Cumulative Distance:</strong> ${(stop.cumulative_km || 0).toFixed(2)} km</div>
              <div style="margin-bottom: 4px;"><strong>Handover:</strong> ${stop.handover_time || '5'} min</div>
              <div style="margin-bottom: 8px;"><strong>Depart:</strong> ${stop.depart_time || ''}</div>
            </div>
          </div>
        `);

        popup.on('close', () => {
          setCurrentPopup(null);
        });

        const markerElement = document.createElement("div");
        markerElement.style.cssText = `
          background: ${color};
          color: white;
          padding: 6px 12px; 
          border-radius: 16px;
          font-size: 12px;
          font-weight: 500;
          border: none;
          box-shadow: 0 1px 3px rgba(0,0,0,0.2);
          cursor: pointer;
          text-align: center;
          min-width: 24px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          white-space: nowrap;
          gap: 6px;
        `;

        const stopCircle = document.createElement("div");
        stopCircle.style.cssText = `
          background: rgba(255,255,255,0.95);
          color: ${color};
          width: 22px;
          height: 22px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 11px;
          font-weight: 700;
          flex-shrink: 0;
          border: 2px solid rgba(255,255,255,0.3);
        `;
        stopCircle.textContent = stopIndex + 1;

        const orderText = document.createElement("span");
        orderText.style.fontWeight = '600';
        orderText.textContent = stop.order || "?";

        markerElement.appendChild(stopCircle);
        markerElement.appendChild(orderText);


        const marker = new mapboxgl.Marker({
          element: markerElement
        })
          .setLngLat([parseFloat(stop.coord.lon), parseFloat(stop.coord.lat)])
          .setPopup(popup)
          .addTo(map.current);

        marker.getElement().addEventListener('click', () => {
          if (currentPopup) {
            currentPopup.remove();
            setCurrentPopup(null);
          }
          setCurrentPopup(popup);

          setTimeout(() => {
            const selectEl = document.getElementById(`drv_${stop.order}`);
            if (selectEl) {
              // Remove existing listener to avoid duplicates
              if (selectEl._changeHandler) {
                selectEl.removeEventListener('change', selectEl._changeHandler);
              }

              // Create new change handler
              selectEl._changeHandler = async (e) => {
                const newDriver = e.target.value;
                const oldDriver = plan.driver;
                const orderId = stop.order;

                if (!newDriver || newDriver === oldDriver) return;

                try {
                  // Close popup immediately
                  if (popup) {
                    popup.remove();
                  }
                  setCurrentPopup(null);

                  // Build locked assignments for ALL current orders, then update the changed one
                  // This prevents the backend from re-clustering other orders
                  const allLockedAssignments = buildLockedAssignmentsFromPlans(
                    plansRef.current,
                    { [orderId]: newDriver }
                  );

                  // Update locked assignments state
                  setLocked(allLockedAssignments);
                  lockedRef.current = allLockedAssignments;

                  toast.info(`Reassigning Order ${orderId} to ${newDriver}…`);

                  const currentDrivers =
                    plansRef.current?.map(p => p.driver).filter(Boolean) || [];

                  const nextDrivers = Array.from(new Set([...currentDrivers, newDriver]));

                  // Debounce: rapid successive reassignments collapse into a single
                  // planRoutes call fired 300 ms after the last change.
                  pendingRebuildDriversRef.current = nextDrivers;
                  if (rebuildTimerRef.current) clearTimeout(rebuildTimerRef.current);
                  rebuildTimerRef.current = setTimeout(() => {
                    rebuildTimerRef.current = null;
                    handleRebuildRoutes(pendingRebuildDriversRef.current);
                  }, 300);

                } catch (error) {
                  console.error('Error during order reassignment:', error);
                  toast.error(`Failed to reassign order: ${error.message}`);
                }
              };

              selectEl.addEventListener('change', selectEl._changeHandler);
            }
          }, 100);
        });

        markers.current.push(marker);
      });
    }

    // Fit map to show all routes and markers
    if (bounds && plans.length > 0 && map.current) {
      // Use setTimeout to ensure all layers are rendered before fitting
      setTimeout(() => {
        map.current.fitBounds(bounds, {
          padding: { top: 80, bottom: 80, left: 80, right: 80 },
          duration: 800,
          maxZoom: 14  // Don't zoom in too much
        });
      }, 100);
    }
  };

  const handleRebuildRoutes = async (driversOverride = []) => {
    const currentPlans = plansRef.current;

    if (!currentPlans || currentPlans.length === 0) {
      toast.error('No existing routes to rebuild');
      return;
    }

    setIsLoading(true);

    try {
      // Collect all existing orders from plans
      const ordersFromPlans = [];

      currentPlans.forEach(plan => {
        plan.stops?.forEach(stop => {
          if (stop && stop.coord?.lat != null && stop.coord?.lon != null) {
            ordersFromPlans.push({
              id: stop.orderId,
              order: stop.order,
              customer: stop.customer,
              total: stop.total,
              coordinates: [parseFloat(stop.coord.lon), parseFloat(stop.coord.lat)],
              timeSlot: selectedTime,
              deliveryDate: selectedDate,
              deliveryStaffUrl: stop.deliveryStaffUrl || '',
              driver: lockedRef.current?.[String(stop.order)]
            });
          }
        });
      });

      if (ordersFromPlans.length === 0) {
        toast.error('No valid orders to rebuild');
        return;
      }

      // Drivers to use
      const driversToUse =
        driversOverride.length > 0
          ? driversOverride
          : Array.from(new Set(currentPlans.map(p => p.driver).filter(Boolean)));

      const driversPayload = driversToUse.map(name => {
        const existing = currentPlans.find(p => p.driver === name);
        const drv = availableDrivers.find(d => d.name === name);

        return {
          name,
          id: existing?.driverId || drv?._id || null,
          color: existing?.color || drv?.color || getDriverColor(name)
        };
      });

      const normalizedLocks = {};
      Object.entries(lockedRef.current || {}).forEach(([orderId, driver]) => {
        normalizedLocks[String(orderId)] = driver;
      });

      const payload = {
        slot: selectedTime,
        date: selectedDate,
        serviceMin: 5,
        drivers: driversPayload,
        orders: ordersFromPlans,
        lockedAssignments: normalizedLocks
      };

      const response = await dispatchAPI.planRoutes(payload);

      if (!response.success || !response.data?.plans) {
        toast.error(response.msg || 'Failed to rebuild routes');
        return;
      }

      const newPlans = response.data.plans;

      setPlans(newPlans);

      setRouteData({
        drivers: newPlans.map(plan => ({
          name: plan.driver,
          totalDistance: `${plan.distance_km.toFixed(2)} km`,
          totalTime: plan.total_hhmm,
          returnETA: plan.return_eta,
          driverLoad: plan.load,
          orderCount: plan.stops?.length || 0
        })),
        plans: newPlans,
        startTime: response.data.start_time,
        returnTimeLatest: response.data.return_time_latest
      });

      setHasUnsavedChanges(true);

      await drawMultipleRoutes(newPlans);

      if (response.data?.rejected_orders > 0) {
        toast.warning(`${response.data.rejected_orders} orders could not be assigned due to capacity constraints.`);
      } else {
        toast.success('Routes rebuilt successfully');
      }

    } catch (err) {
      console.error('Rebuild failed:', err);
      toast.error('Failed to rebuild routes');
    } finally {
      setIsLoading(false);
    }
  };

  // Handle driver change
  const handleDriverChange = async (oldDriver, newDriver, rowIndex) => {
    if (!newDriver || newDriver === oldDriver) return;

    try {
      setIsLoading(true);

      const currentPlans = plans || [];
      const updatedPlans = [...currentPlans];
      const newDriverIndex = updatedPlans.findIndex(plan => plan.driver === newDriver);
      const newDriverInfo = availableDrivers.find(d => d.name === newDriver);
      const oldDriverInfo = availableDrivers.find(d => d.name === oldDriver);

      if (updatedPlans[rowIndex]) {
        updatedPlans[rowIndex] = {
          ...updatedPlans[rowIndex],
          driver: newDriver,
          driverId: newDriverInfo?._id || updatedPlans[rowIndex].driverId,
          color: getDriverColor(newDriver)
        };
      }

      if (newDriverIndex !== -1 && newDriverIndex !== rowIndex) {
        updatedPlans[newDriverIndex] = {
          ...updatedPlans[newDriverIndex],
          driver: oldDriver,
          driverId: oldDriverInfo?._id || updatedPlans[newDriverIndex].driverId,
          color: getDriverColor(oldDriver)
        };
      }

      // Update state with computed plans
      setPlans(updatedPlans);

      // Also update routeData.drivers to keep table in sync
      setRouteData(prev => {
        if (!prev?.drivers) return prev;
        const updatedDrivers = [...prev.drivers];
        const newDriverIdx = updatedDrivers.findIndex(d => d.name === newDriver);

        if (newDriverIdx !== -1 && newDriverIdx !== rowIndex) {
          // Swap the entire row data between the two drivers
          const oldRowData = { ...updatedDrivers[rowIndex] };
          const newRowData = { ...updatedDrivers[newDriverIdx] };

          // Swap names and keep other stats with the new driver
          updatedDrivers[rowIndex] = { ...oldRowData, name: newDriver };
          updatedDrivers[newDriverIdx] = { ...newRowData, name: oldDriver };
        } else if (updatedDrivers[rowIndex]) {
          // Just update the name if newDriver doesn't have an existing row
          updatedDrivers[rowIndex] = { ...updatedDrivers[rowIndex], name: newDriver };
        }

        return { ...prev, drivers: updatedDrivers };
      });

      // Redraw map with updated driver assignments
      if (updatedPlans.length > 0) {
        await drawMultipleRoutes(updatedPlans);
      }

      toast.success(`✅ Successfully swapped ${oldDriver} with ${newDriver}`);
      setHasUnsavedChanges(true);

    } catch (error) {
      console.error('Error swapping drivers:', error);
      toast.error(`Failed to swap drivers: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    lockedRef.current = locked;
  }, [locked]);

  // Toggle order selection
  const toggleOrderSelection = (orderNumber) => {
    setSelectedOrders(prev => {
      if (prev.includes(orderNumber)) {
        return prev.filter(o => o !== orderNumber);
      } else {
        return [...prev, orderNumber];
      }
    });
  };

  // Select all orders
  const selectAllOrders = () => {
    // Get only unused orders (not in usedOrderIds)
    const unusedOrders = fetchedOrders.filter(order => !usedOrderIds.has(order.order));
    const unusedOrderNumbers = unusedOrders.map(order => order.order);

    // Check if all unused orders are already selected
    const allUnusedSelected = unusedOrderNumbers.every(orderNum => selectedOrders.includes(orderNum));

    if (allUnusedSelected && selectedOrders.length > 0) {
      // Deselect all
      setSelectedOrders([]);
    } else {
      // Select only unused orders
      setSelectedOrders(unusedOrderNumbers);
    }
  };

  // Reset
  const handleReset = () => {
    setFetchedOrders([]);
    setSelectedOrders([]);
    setRouteData(null);
    setPlans([]);
    setSelectedDrivers([]);
    setExistingRouteId(null);
    existingRouteIdRef.current = null;
    setSlotConflict(false);
    setUsedOrderIds(new Set());
    setHasUnsavedChanges(false);
    setHasFetchedOnce(false);
    setFetchedOrders([]);
    setSelectedOrders([]);
    setRouteData(null);
    clearMarkers(false);

    routeLayers.current.forEach(layerId => {
      if (map.current.getLayer(layerId)) {
        map.current.removeLayer(layerId);
      }
      if (map.current.getSource(layerId)) {
        map.current.removeSource(layerId);
      }
    });
    routeLayers.current = [];

    if (map.current) {
      map.current.flyTo({
        center: DEPOT,
        zoom: 10.8
      });
    }

    // Re-add depot marker
    if (map.current && !depotMarker.current) {
      depotMarker.current = new mapboxgl.Marker({ color: '#000000', scale: 1.2 })
        .setLngLat(DEPOT)
        .setPopup(new mapboxgl.Popup().setHTML(`
          <div style="padding: 8px; text-align: center;">
            <h3 style="margin: 0 0 4px 0; color: #FE8403; font-size: 14px; font-weight: 600;">🏢 ${DEPOT_LABEL}</h3>
            <p style="margin: 0; color: #666; font-size: 12px;">Starting Point</p>
          </div>
        `))
        .addTo(map.current);
    }
  };

  // Reset with unsaved warning check
  const handleResetWithWarning = () => {
    if (hasUnsavedChanges) {
      setShowResetModal(true);
    } else {
      handleReset();
    }
  };

  // Confirm reset (discard changes)
  const confirmReset = () => {
    setShowResetModal(false);
    handleReset();
  };

  // Get driver color 
  const getDriverColor = (driverName) => {
    const driver = availableDrivers.find(d => d.name === driverName);
    if (driver && driver.color) {
      return driver.color;
    }
    const palette = ['#FE8403', '#ef4444', '#10b981', '#3b82f6', '#8b5cf6', '#06b6d4'];
    const index = availableDrivers.findIndex(d => d.name === driverName);
    return index >= 0 ? palette[index % palette.length] : '#FE8403';
  };

  // Handle save route
  const handleSaveRoute = async () => {
    if (!plans || plans.length === 0) {
      toast.error('No route to save');
      return;
    }

    if (isSavingRef.current) return;
    isSavingRef.current = true;

    setIsSaveLoading(true);

    try {
      const existingId = existingRouteIdRef.current || existingRouteId;
      const slotId = fetchedOrders.find(o => o.timeSlotId)?.timeSlotId || null;
      const isAdditionalRoute = existingId && shouldCreateAdditionalRoute(plans, usedOrderIds);

      if (existingId && !isAdditionalRoute) {
        await updateRouteInHistory(plans, selectedTime, selectedDate, slotId);
        toast.success('Route updated successfully!');
      } else {
        const result = await saveRouteToHistory(plans, selectedTime, selectedDate, slotId);
        const routeId = result?.data?._id || result?.data?.route_id;

        if (routeId) {
          if (!existingId) {
            setExistingRouteId(routeId);
            existingRouteIdRef.current = routeId;
          }
          toast.success(isAdditionalRoute ? 'Additional route saved successfully!' : 'Route saved successfully!');
        }
      }

      setHasUnsavedChanges(false);
      markOrdersAsUsed(plans);
      if (selectedTime !== 'ALL ORDERS') {
        await checkUsedOrdersAndReturn(selectedTime, selectedDate);
      }
    } catch (error) {
      console.error('Error saving route:', error);
      toast.error('Failed to save route');
    } finally {
      setIsSaveLoading(false);
      isSavingRef.current = false;
    }
  };

  // Handle delete route confirmation
  const confirmDeleteRoute = async () => {
    setIsDeleting(true);
    try {
      const currentRouteId = existingRouteIdRef.current || existingRouteId;

      if (currentRouteId) {
        await dispatchAPI.deleteRouteHistory(currentRouteId);
        toast.success('Route deleted successfully!');
      }

      // Reset everything
      handleReset();
      setShowDeleteModal(false);
    } catch (error) {
      console.error('Error deleting route:', error);
      toast.error('Failed to delete route');
    } finally {
      setIsDeleting(false);
    }
  };

  const markOrdersAsUsed = (savedPlans) => {
    const newUsed = new Set(usedOrderIds);

    // collect orders from plans
    savedPlans.forEach(plan => {
      plan.stops?.forEach(stop => {
        if (stop.order) newUsed.add(stop.order);
      });
    });

    // update used orders
    setUsedOrderIds(newUsed);

    // unselect those orders
    setSelectedOrders(prev =>
      prev.filter(orderNo => !newUsed.has(orderNo))
    );
  };

  // Check if route can be shared/copied (warn if unsaved)
  const checkBeforeShare = (driverName, actionType) => {
    if (hasUnsavedChanges) {
      setPendingAction({ type: actionType, driver: driverName });
      setShowUnsavedModal(true);
      return;
    }
    handleSaveAndShare(driverName, actionType);
  };

  // Proceed with share after warning acknowledged
  const proceedWithShare = async () => {
    if (pendingAction) {
      handleSaveAndShare(pendingAction.driver, pendingAction.type);
      setPendingAction(null);
    }
    setShowUnsavedModal(false);
  };

  // Grouped text output for copy/WhatsApp 
  const grouped = useMemo(() => {
    const m = {};
    plans.forEach(p => {
      if (!m[p.driver]) m[p.driver] = [];

      const driverHeader = [
        '______________________________',
        `Delivery Staff : ${p.driver}`,
        '______________________________'
      ].join('\n');

      const orderLines = p.stops.filter(s => s && s.coord && s.coord.lat != null && s.coord.lon != null).map((s, idx) => [
        `🔢 Stop ${idx + 1}`,
        `📦 ORDER #${s.order || ''}`,
        '-------------------------------',
        `Customer: ${s.customer || ''}`,
        `Order Total: ${s.total || ''} QR`,
        `Customer location: https://maps.google.com/?q=${s.coord.lat},${s.coord.lon}`,
        `Confirm Delivery Status: ${s.deliveryStaffUrl || ''}${IS_ALFAJR && s.deliveryStaffUrl ? `&rid=${p.driverId || ''}` : ''}`,
        '______________________________'
      ].join('\n'));

      let routeOverviewUrl = `${API_CONFIG.ADMIN_BASE_URL}/route-overview?driver=${encodeURIComponent(p.driver)}`;

      const currentRouteId = existingRouteIdRef.current || existingRouteId;
      if (currentRouteId) {
        routeOverviewUrl += `&routeId=${currentRouteId}`;
      }

      const routeOverviewLine = [
        '🗺️ ROUTE OVERVIEW',
        '-------------------------------',
        `Route overview: ${routeOverviewUrl}`,
        '______________________________'
      ].join('\n');

      const driverOutput = [driverHeader, ...orderLines, routeOverviewLine].join('\n');
      m[p.driver].push(driverOutput);
    });
    return m;
  }, [plans, existingRouteId]);

  // Copy function
  const copyToClipboard = async (txt) => {
    try {
      await navigator.clipboard.writeText(txt);
      toast.success('Copied to clipboard!');
      setTimeout(() => toast.dismiss(), 1500);
    } catch (error) {
      toast.error('Failed to copy to clipboard');
    }
  };

  const shareToWhatsApp = (txt, driverName) => {
    const encodedText = encodeURIComponent(txt);
    // Find driver's phone number from selectedDrivers
    const driver = selectedDrivers.find(d => d.name === driverName);
    const phone = driver?.phone?.replace(/[^\d]/g, '');

    // If driver has phone number, direct to that number, otherwise generic share
    const waUrl = phone ? `https://wa.me/${phone}?text=${encodedText}` : `https://wa.me/?text=${encodedText}`;
    window.open(waUrl, '_blank');
  };

  // Generate message for a driver with a specific routeId
  const generateDriverMessage = (driverName, routeId) => {
    const driverPlan = plans.find(p => p.driver === driverName);
    if (!driverPlan) return '';

    const driverHeader = [
      '______________________________',
      `Delivery Staff : ${driverPlan.driver}`,
      '______________________________'
    ].join('\n');

    const orderLines = (driverPlan.stops || [])
      .filter(s => s && s.coord && s.coord.lat != null && s.coord.lon != null)
      .map((s, idx) => [
        `🔢 Stop ${idx + 1}`,
        `📦 ORDER #${s.order || ''}`,
        '-------------------------------',
        `Customer: ${s.customer || ''}`,
        `Order Total: ${s.total || ''} QR`,
        `Customer location: https://maps.google.com/?q=${s.coord.lat},${s.coord.lon}`,
        `Confirm Delivery Status: ${s.deliveryStaffUrl || ''}`,
        '______________________________'
      ].join('\n'));

    let routeOverviewUrl = `${API_CONFIG.ADMIN_BASE_URL}/route-overview?driver=${encodeURIComponent(driverPlan.driver)}`;
    if (routeId) {
      routeOverviewUrl += `&routeId=${routeId}`;
    }

    const routeOverviewLine = [
      '🗺️ ROUTE OVERVIEW',
      '-------------------------------',
      `Route overview: ${routeOverviewUrl}`,
      '______________________________'
    ].join('\n');

    return [driverHeader, ...orderLines, routeOverviewLine].join('\n');
  };

  // Handle save and share 
  // Routes are already auto-saved by backend, but we update if route exists
  const handleSaveAndShare = async (driverName, actionType) => {
    try {
      const latestPlans = plansRef.current;

      if (!latestPlans || latestPlans.length === 0) {
        toast.error('No route to save');
        return;
      }

      let finalRouteId = existingRouteIdRef.current || existingRouteId;
      const isAdditionalRoute = finalRouteId && shouldCreateAdditionalRoute(latestPlans, usedOrderIds);

      // Always save latest changes before sharing
      if (hasUnsavedChanges) {
        try {
          if (finalRouteId && !isAdditionalRoute) {
            await updateRouteInHistory(latestPlans, selectedTime, selectedDate);
          } else {
            const result = await saveRouteToHistory(latestPlans, selectedTime, selectedDate);
            const newRouteId = result?.data?._id || result?.data?.route_id;

            if (newRouteId) {
              if (!finalRouteId) {
                setExistingRouteId(newRouteId);
                existingRouteIdRef.current = newRouteId;
              }
              finalRouteId = isAdditionalRoute ? newRouteId : (finalRouteId || newRouteId);
            }
          }

          setHasUnsavedChanges(false);
          markOrdersAsUsed(latestPlans);

          // Refresh used orders list after successful save
          if (selectedTime !== 'ALL ORDERS') {
            await checkUsedOrdersAndReturn(selectedTime, selectedDate);
          }
        } catch (saveError) {
          console.error('Auto-save before share failed:', saveError);
          return;
        }
      }

      // Generate final message using latest routeId
      const messageText = generateDriverMessage(driverName, finalRouteId);

      // Perform share action
      if (actionType === 'copy') {
        await copyToClipboard(messageText);
      } else if (actionType === 'whatsapp') {
        shareToWhatsApp(messageText, driverName);
      }
    } catch (error) {
      console.error('handleSaveAndShare error:', error);
      toast.error('An error occurred while sharing');
    }
  };

  // Toggle driver selection 
  const toggleDriverSelection = (driverName) => {
    // Reset routes if they exist, but keep orders
    if (routeData && routeData.plans && routeData.plans.length > 0) {
      resetRoutesOnly();
    }

    setSelectedDrivers(prev => {
      const driver = availableDrivers.find(d => d.name === driverName);
      if (!driver) return prev;

      if (prev.some(d => d.name === driverName)) {
        return prev.filter(d => d.name !== driverName);
      } else {
        return [...prev, driver];
      }
    });
  };

  // Format date for display
  const formatDisplayDate = (dateString) => {
    return formatDateOnly(dateString);
  };

  // Get formatted date
  const getFormattedDate = () => {
    const date = new Date(selectedDate);
    return date.toISOString().split('T')[0];
  };

  return (
    <div className="dispatch-container">
      <div className="dispatch-header">
        <div className="dispatch-header-title-container">
          <h1 className="dispatch-header-title">Dispatch Planner</h1>
          <p className="dispatch-header-subtitle">Plan and assign delivery routes for orders</p>
        </div>
        {onPageChange && helpers.hasPermission(adminData, ADMIN_PERMISSIONS.VIEW_DISPATCH_HISTORY) && (
          <button
            className="dispatch-history-nav-btn"
            onClick={() => onPageChange('dispatch-history')}
          >
            <History size={16} />
            View History
          </button>
        )}
      </div>

      {/* Main Content */}
      <div className="dispatch-content">
        {/* Left Panel */}
        <div className="dispatch-left-panel">
          {/* Control Panel */}
          <div className="dispatch-control-panel">
            {fetchedOrders.length > 0 && (
              <div className="dispatch-status-message">
                <CheckCircle size={16} className="dispatch-status-icon" />
                <span>{fetchedOrders.length} orders loaded for {selectedTime} on {getFormattedDate()}</span>
              </div>
            )}

            <div className="dispatch-date-time-controls">
              <div className="dispatch-date-input-wrapper">
                <input
                  type="date"
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  className="dispatch-date-input"
                />
              </div>

              <div className="dispatch-time-select-wrapper">
                <select
                  value={selectedTime}
                  onChange={(e) => setSelectedTime(e.target.value)}
                  className="dispatch-time-select"
                >
                  {timeSlots.map(slot => (
                    <option key={slot.name} value={slot.name}>{slot.name}</option>
                  ))}
                </select>
              </div>
            </div>

            <button
              className="dispatch-fetch-button"
              onClick={fetchOrders}
              disabled={isLoading}
            >
              {isLoading ? (
                <span className="dispatch-loading" />
              ) : (
                <Download size={16} />
              )}
              Fetch orders
            </button>

            <div className="dispatch-fetched-orders">
              <div className="dispatch-orders-header">
                <h3 className="dispatch-section-title">
                  Fetched confirmed orders ({fetchedOrders.length})
                </h3>
                {fetchedOrders.length > 0 && (() => {
                  const unusedCount = fetchedOrders.filter(o => !usedOrderIds.has(o.order)).length;
                  return (
                    <label className="dispatch-select-all-label">
                      <input
                        type="checkbox"
                        checked={selectedOrders.length === unusedCount && unusedCount > 0}
                        onChange={selectAllOrders}
                        className="dispatch-order-checkbox"
                        disabled={unusedCount === 0}
                      />
                      {unusedCount === fetchedOrders.length ? 'All Selected' : `Select All (${unusedCount})`}
                    </label>
                  );
                })()}
              </div>

              {fetchedOrders.length === 0 ? (
                <p className="dispatch-placeholder-text">
                  {hasFetchedOnce ? 'No orders found for this date/slot' : 'Fetch orders to see them here'}
                </p>
              ) : (
                <div className="dispatch-order-list">
                  {fetchedOrders.map((order) => {
                    const isUsed = usedOrderIds.has(order.order);
                    // Check if order is in current plans
                    const inRoute = plans.some(p => p.stops && p.stops.some(s => s.order === order.order));
                    return (
                      <div
                        key={order.order}
                        className={`dispatch-order-item ${selectedOrders.includes(order.order) ? 'dispatch-order-selected' : ''} ${isUsed ? 'dispatch-order-used' : ''} ${inRoute ? 'dispatch-order-in-route' : ''}`}
                        title={isUsed ? 'Order saved in history' : inRoute ? 'Order in current plan' : ''}
                        onClick={() => !isUsed && toggleOrderSelection(order.order)}
                        style={{ cursor: isUsed ? 'not-allowed' : 'pointer' }}
                      >
                        <input
                          type="checkbox"
                          checked={selectedOrders.includes(order.order)}
                          onChange={() => toggleOrderSelection(order.order)}
                          onClick={(e) => e.stopPropagation()}
                          className="dispatch-order-checkbox"
                          disabled={isUsed}
                        />
                        <div className="dispatch-order-info">
                          <div className="dispatch-order-header">
                            <p className="dispatch-order-number">#{order.order}</p>
                            {isUsed && (
                              <span className="dispatch-order-status" style={{ backgroundColor: '#fbbf24', color: '#92400e', padding: '2px 6px', borderRadius: '4px', fontSize: '10px', fontWeight: 'bold' }}>USED</span>
                            )}
                            {!isUsed && inRoute && (
                              <span className="dispatch-order-status" style={{ backgroundColor: '#dbeafe', color: '#1e40af', padding: '2px 6px', borderRadius: '4px', fontSize: '10px', fontWeight: 'bold' }}>IN ROUTE</span>
                            )}
                            {order.status && order.status !== 'confirmed' && !isUsed && !inRoute && (
                              <span className="dispatch-order-status">{order.status}</span>
                            )}
                          </div>
                          <p className="dispatch-order-customer">{order.customer}</p>
                          <p className="dispatch-order-details">
                            {order.total} QR • {parseFloat(order.coordinates[1]).toFixed(4)}, {parseFloat(order.coordinates[0]).toFixed(4)}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {selectedOrders.length > 0 && (
              <div className="dispatch-selected-orders">
                <h3 className="dispatch-section-title dispatch-selected-title">
                  Selected Orders ({selectedOrders.length})
                </h3>
                <div className="dispatch-selected-list">
                  {fetchedOrders
                    .filter(order => selectedOrders.includes(order.order))
                    .map((order) => (
                      <div key={order.order} className="dispatch-selected-item">
                        <p className="dispatch-selected-order-number">Order #{order.order}</p>
                        <p className="dispatch-selected-detail">Customer: {order.customer}</p>
                        <p className="dispatch-selected-detail">Total: {order.total} QR</p>
                        <p className="dispatch-selected-detail">
                          Coordinates: {parseFloat(order.coordinates[1]).toFixed(4)}, {parseFloat(order.coordinates[0]).toFixed(4)}
                        </p>
                      </div>
                    ))}
                </div>
              </div>
            )}

            <div className="dispatch-drivers-section">
              <div className="dispatch-drivers-header">
                <Users size={16} className="dispatch-section-icon" />
                <span className="dispatch-section-label">Available Drivers</span>
              </div>
              {isLoadingDrivers ? (
                <div className="dispatch-drivers-loading">
                  <Loader2 size={20} className="dispatch-loading-spinner" />
                  <span>Loading drivers...</span>
                </div>
              ) : availableDrivers.length === 0 ? (
                <div className="dispatch-drivers-empty">
                  <Users size={24} className="dispatch-empty-icon" />
                  <span>No drivers available</span>
                </div>
              ) : (
                <div className="dispatch-driver-badges">
                  {availableDrivers.map((driver) => {
                    const isSelected = selectedDrivers.some(d => d.name === driver.name);
                    const driverColor = getDriverColor(driver.name);
                    return (
                      <div
                        key={driver.name}
                        onClick={() => toggleDriverSelection(driver.name)}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8,
                          padding: '6px 12px',
                          borderRadius: 999,
                          background: isSelected ? '#F9F1E6' : '#f3f4f6',
                          border: isSelected ? '1px solid #FE8403' : '1px solid #d1d5db',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          transform: isSelected ? 'scale(1.02)' : 'scale(1)',
                          fontWeight: isSelected ? 600 : 500,
                          color: isSelected ? '#FE8403' : '#374151',
                          fontSize: '14px',
                          flexShrink: 0
                        }}
                      >
                        <div style={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          backgroundColor: driverColor
                        }}></div>
                        {driver.name.toUpperCase()}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {Object.keys(grouped).length === 0 && (
              <div className="dispatch-build-info">
                <MapPin size={16} className="dispatch-info-icon" />
                <span>Build routes to see outputs...</span>
              </div>
            )}

            {Object.keys(grouped).length > 0 && (
              <div style={{ marginBottom: '16px', maxHeight: '300px', overflowY: 'auto' }}>
                {Object.entries(grouped).map(([drv, lines]) => {
                  const color = getDriverColor(drv);
                  const txt = lines.join('\n');
                  return (
                    <details key={drv} className="card" style={{ margin: '8px 0', border: '1px solid #e5e7eb', borderRadius: '8px' }}>
                      <summary style={{ padding: '10px 12px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <div style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: color }}></div>
                          <b>{drv.toUpperCase()} <span style={{ fontSize: '12px', color: '#6b7280' }}>({plans.find(p => p.driver === drv)?.stops?.length || 0})</span></b>
                        </div>
                        <div style={{ display: 'flex', gap: 8 }} onClick={(e) => e.stopPropagation()}>
                          <button
                            onClick={() => checkBeforeShare(drv, 'copy')}
                            title="Copy & Save"
                            style={{
                              background: 'none',
                              border: 'none',
                              cursor: 'pointer',
                              padding: '4px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}
                          >
                            <Copy size={16} style={{ color: '#6b7280' }} />
                          </button>
                          <button
                            onClick={() => checkBeforeShare(drv, 'whatsapp')}
                            title={`WhatsApp ${drv} & Save`}
                            style={{
                              background: 'none',
                              border: 'none',
                              cursor: 'pointer',
                              padding: '4px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}
                          >
                            <MessageCircle size={16} style={{ color: '#25D366' }} />
                          </button>
                        </div>
                      </summary>
                      <div style={{ padding: '8px 12px' }}>
                        <div style={{ background: '#fafafa', borderRadius: '6px', padding: '8px' }}>
                          <pre style={{ margin: 0, fontSize: '12px', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{txt}</pre>
                        </div>
                      </div>
                    </details>
                  );
                })}
              </div>
            )}

            {slotConflict && (
              <div style={{
                padding: '12px',
                marginBottom: '12px',
                backgroundColor: '#FEF3C7',
                border: '1px solid #FCD34D',
                borderRadius: '8px',
                fontSize: '14px',
                color: '#92400E'
              }}>
                ⚠️ A route already exists for {selectedTime} on {selectedDate}. You can build additional routes for new orders.
              </div>
            )}

            <div className="dispatch-action-buttons">
              {plans.length === 0 ? (
                <button
                  className="dispatch-build-routes-button"
                  onClick={buildRoutes}
                  disabled={isLoading || selectedOrders.length === 0}
                  title="Build routes for selected orders"
                >
                  {isLoading ? 'Building...' : 'Build routes'}
                </button>
              ) : (
                <>
                  <button
                    className="dispatch-save-route-button"
                    onClick={handleSaveRoute}
                    disabled={isSaveLoading || (!hasUnsavedChanges && existingRouteId)}
                  >
                    {isSaveLoading ? 'Saving...' : (existingRouteId && !hasUnsavedChanges) ? 'Saved' : hasUnsavedChanges ? 'Save Changes' : 'Save Route'}
                  </button>
                  <button
                    className="dispatch-delete-route-button"
                    onClick={() => setShowDeleteModal(true)}
                    disabled={isDeleting}
                  >
                    Delete Route
                  </button>
                </>
              )}
              <button
                className="dispatch-refresh-button"
                onClick={handleResetWithWarning}
                disabled={isLoading}
              >
                <RotateCcw size={18} />
              </button>
            </div>
          </div>
        </div>

        {/* Right Panel - Map */}
        <div className="dispatch-right-panel">
          <div ref={mapContainer} className="dispatch-map-container" />

          {isLoading && (
            <div className="dispatch-map-loading-overlay">
              <div className="dispatch-map-loading-spinner"></div>
              <div className="dispatch-map-loading-text">Working...</div>
            </div>
          )}


          {/* Driver Totals Table */}
          <div className="dispatch-driver-totals">
            {/* Header with title and summary boxes */}
            <div className="dispatch-totals-header-row">
              <div className="dispatch-totals-title">
                Driver Totals (drive + 5 min per stop)<br />
                Hard cap per slot: 120 min
              </div>
              <div className="dispatch-totals-badges">
                <div className="dispatch-totals-badge">
                  <b>Start:</b> {routeData?.startTime || '-'}
                </div>
                <div className="dispatch-totals-badge">
                  <b>ETA:</b> {routeData?.returnTimeLatest || '-'}
                </div>
                <div className="dispatch-totals-badge">
                  <b>Date:</b> {formatDisplayDate(selectedDate)}
                </div>
              </div>
            </div>

            {isMobile ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', width: '100%', padding: '8px', boxSizing: 'border-box' }}>
                {routeData?.drivers && routeData.drivers.length > 0 ? routeData.drivers.map((driver, index) => (
                  <div key={index} style={{
                    background: '#f9fafb',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    padding: '10px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '6px'
                  }}>
                    {/* Driver Header */}
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontWeight: '600', fontSize: '16px' }}>
                        <span style={{ display: 'inline-block', width: '12px', height: '12px', borderRadius: '50%', background: getDriverColor(driver.name) }} />
                        <select
                          value={driver.name}
                          onChange={(e) => handleDriverChange(driver.name, e.target.value, index)}
                          style={{
                            background: 'transparent',
                            border: 'none',
                            fontSize: '16px',
                            fontWeight: '600',
                            color: '#1f2937',
                            cursor: 'pointer',
                            outline: 'none',
                            padding: '2px 4px',
                            borderRadius: '4px',
                            minWidth: '80px'
                          }}
                        >
                          {selectedDrivers.map(d => (
                            <option key={d.name} value={d.name}>{d.name}</option>
                          ))}
                        </select>
                      </div>
                      <span style={{
                        background: driver.driverLoad === 'Normal' ? '#ecfdf5' : driver.driverLoad === 'Underloaded' ? '#fef9c3' : '#fef2f2',
                        color: driver.driverLoad === 'Normal' ? '#065f46' : driver.driverLoad === 'Underloaded' ? '#854d0e' : '#991b1b',
                        padding: '4px 8px',
                        borderRadius: '12px',
                        fontSize: '13px',
                        fontWeight: '500',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px'
                      }}>
                        <span style={{ display: 'inline-block', width: '6px', height: '6px', borderRadius: '50%', background: driver.driverLoad === 'Normal' ? '#10b981' : driver.driverLoad === 'Underloaded' ? '#eab308' : '#ef4444' }} />
                        {driver.driverLoad}
                      </span>
                    </div>

                    {/* Stats Grid */}
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: '1fr 1fr',
                      gap: '6px',
                      fontSize: '13px'
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ color: '#6b7280' }}>Distance:</span>
                        <span style={{ fontWeight: '500' }}>{driver.totalDistance}</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ color: '#6b7280' }}>Orders:</span>
                        <span style={{ fontWeight: '500' }}>{driver.orderCount}</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ color: '#6b7280' }}>Time:</span>
                        <span style={{ fontWeight: '500' }}>{driver.totalTime}</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ color: '#6b7280' }}>Return:</span>
                        <span style={{ fontWeight: '500' }}>{driver.returnETA}</span>
                      </div>
                    </div>
                  </div>
                )) : (
                  <div style={{
                    background: '#f9fafb',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    padding: '20px',
                    textAlign: 'center',
                    color: '#6b7280',
                    fontSize: '14px'
                  }}>
                    No routes built yet. Build routes to see driver totals.
                  </div>
                )}
              </div>
            ) : (
              /* Desktop: Table layout */
              <div className="dispatch-totals-table-wrapper">
                <table className="dispatch-totals-table">
                  <thead>
                    <tr>
                      <th style={{ width: '12vw' }}>Driver</th>
                      <th style={{ width: '12%' }}>Total Distance</th>
                      <th style={{ width: '15%' }}>Total Time</th>
                      <th style={{ width: '12%' }}>Return ETA</th>
                      <th style={{ width: '12%' }}>Driver Load</th>
                      <th style={{ width: '10%' }}># Orders</th>
                    </tr>
                  </thead>
                  <tbody>
                    {routeData?.drivers && routeData.drivers.length > 0 ? (
                      routeData.drivers.map((driver, index) => (
                        <tr key={index}>
                          <td>
                            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                              <span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: '50%', background: getDriverColor(driver.name) }} />
                              <select
                                value={driver.name}
                                onChange={(e) => handleDriverChange(driver.name, e.target.value, index)}
                                style={{
                                  background: 'transparent',
                                  border: '1px solid #d1d5db',
                                  fontSize: '14px',
                                  fontWeight: '500',
                                  color: '#1f2937',
                                  cursor: 'pointer',
                                  outline: 'none',
                                  padding: '4px 8px',
                                  borderRadius: '4px',
                                  minWidth: '100px'
                                }}
                              >
                                {selectedDrivers.map(d => (
                                  <option key={d.name} value={d.name}>{d.name}</option>
                                ))}
                              </select>
                            </div>
                          </td>
                          <td>{driver.totalDistance}</td>
                          <td>{driver.totalTime}</td>
                          <td>{driver.returnETA}</td>
                          <td>
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                              <span style={{ display: 'inline-block', width: 9, height: 9, borderRadius: '50%', background: driver.driverLoad === 'Normal' ? '#10b981' : '#ef4444' }} />
                              {driver.driverLoad}
                            </span>
                          </td>
                          <td>{driver.orderCount}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="6" style={{ textAlign: 'center', color: '#6b7280', fontStyle: 'italic', padding: '20px' }}>
                          No routes built yet. Build routes to see driver totals.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Delete Route Confirmation Modal */}
      {showDeleteModal && (
        <div className="dispatch-modal-overlay">
          <div className="dispatch-modal-box dispatch-delete-modal">
            <div className="dispatch-modal-header">
              <h2>Confirm Delete</h2>
              <button className="dispatch-modal-close-btn" onClick={() => setShowDeleteModal(false)}>
                <X size={20} />
              </button>
            </div>
            <div className="dispatch-modal-content">
              <p>Are you sure you want to delete this route?</p>
              <p className="dispatch-modal-warning">This action cannot be undone.</p>
            </div>
            <div className="dispatch-modal-actions">
              <button
                className="dispatch-modal-cancel-btn"
                onClick={() => setShowDeleteModal(false)}
                disabled={isDeleting}
              >
                Cancel
              </button>
              <button
                className="dispatch-modal-delete-btn"
                onClick={confirmDeleteRoute}
                disabled={isDeleting}
              >
                {isDeleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Unsaved Changes Warning Modal */}
      {showUnsavedModal && (
        <div className="dispatch-modal-overlay">
          <div className="dispatch-modal-box">
            <div className="dispatch-modal-header">
              <h2>Unsaved Changes</h2>
              <button className="dispatch-modal-close-btn" onClick={() => { setShowUnsavedModal(false); setPendingAction(null); }}>
                <X size={20} />
              </button>
            </div>
            <div className="dispatch-modal-content">
              <p>You have unsaved changes. Would you like to save before sharing?</p>
            </div>
            <div className="dispatch-modal-actions">
              <button
                className="dispatch-modal-cancel-btn"
                onClick={() => { setShowUnsavedModal(false); setPendingAction(null); }}
              >
                Cancel
              </button>
              <button
                className="dispatch-modal-save-btn"
                onClick={proceedWithShare}
              >
                Save & Share
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reset Confirmation Modal */}
      {showResetModal && (
        <div className="dispatch-modal-overlay">
          <div className="dispatch-modal-box">
            <div className="dispatch-modal-header">
              <h2>Unsaved Changes</h2>
              <button className="dispatch-modal-close-btn" onClick={() => setShowResetModal(false)}>
                <X size={20} />
              </button>
            </div>
            <div className="dispatch-modal-content">
              <p>You have unsaved changes. Are you sure you want to reset?</p>
              <p className="dispatch-modal-warning">All changes will be lost.</p>
            </div>
            <div className="dispatch-modal-actions">
              <button
                className="dispatch-modal-cancel-btn"
                onClick={() => setShowResetModal(false)}
              >
                Cancel
              </button>
              <button
                className="dispatch-modal-delete-btn"
                onClick={confirmReset}
              >
                Discard & Reset
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DispatchPlanner;